import CSInput from "@components/Form/CSInput"
import CSSelect from "@components/Form/CSSelect"
import Localization from "@localization/Index"
import { useMobileMediaQuery } from "@utils/MediaComponents"
import { useEffect, useState } from "react"
import Form from "react-bootstrap/esm/Form"

type Props = {
    client?: true,
    pageIndex: number,
    pageSize?: number,
    pageSizeSelect?: Array<number>,
    totalItems: number,
    totalFiltered: number,
    totalFilteredPages: number,
    onPageChange?: (page:number)=>void,
    onPageSizeChange?: (pageSize:number)=>void,
    enableGotoPage?: "OFF"|"CHANGE"|"BUTTON",
    data_cy?:string
    disabled?: boolean
}

const CSPagination = (props:Props) =>{
    const {pageIndex, pageSizeSelect, totalItems, totalFiltered, totalFilteredPages, onPageChange, onPageSizeChange, enableGotoPage="BUTTON", disabled=false, client } = props
    const params = new URLSearchParams(window.location.search)
    const [specificPage, setSpecificPage] = useState<string>(params.get("_p") ?? "1")
    const isMobile = useMobileMediaQuery()

    // define some boolean to build the widget
    const canSkipToFirst = (totalFilteredPages>1 && pageIndex>1) 
    const canSkipToLast = (totalFilteredPages>1 && pageIndex<totalFilteredPages) 
    const canGoNext = (totalFilteredPages>1 && pageIndex<totalFilteredPages)
    const canGoPrevious = (totalFilteredPages>1 && pageIndex>1)
    const showEllipsisPrevious = (totalFilteredPages>5 && pageIndex>5)
    const showEllipsisNext = (totalFilteredPages>5 && pageIndex+4<totalFilteredPages)

    const gotoPage = (page:number) =>{
        if(enableGotoPage!=="OFF") { setSpecificPage(page.toString())}
        return onPageChange?.(page)
    }

    const gotoSpecificPage = () =>{
        let intPage = parseInt(specificPage)
        if(isNaN(intPage) || intPage<=0) {
            intPage = 1
        }
        if(intPage>totalFilteredPages) {
            intPage = totalFilteredPages
        }
        return gotoPage(intPage)
    }

    useEffect(()=>{
        if(enableGotoPage==="CHANGE") {gotoSpecificPage()}
    }, [specificPage])

    useEffect(()=>{
        if(client) {
            setSpecificPage("1")
            return onPageChange?.(1)
        }
    },[totalFilteredPages])

    if(isMobile) {
        const pageInfoLabel = `${pageIndex} / ${totalFilteredPages}`
        const itemsInfoLabel =`${totalFiltered>=0 && totalFiltered<totalItems?totalFiltered:totalItems} ${Localization.TABLE.RISULTATI}`

        return <div className="d-flex align-items-center justify-content-between">
            <div className="text-start fs-4 fw-bold">
                {itemsInfoLabel}
            </div>
            <div className="d-flex align-items-center justify-content-end">
                <button onClick={()=>gotoPage(pageIndex-1)} className="btn btn-sm btn-primary mx-2 my-1" disabled={disabled || !canGoPrevious}>
                    <i className="bi-chevron-left text-white" />
                </button>
                <span className="mx-3 fw-bold">{pageInfoLabel}</span>
                <button onClick={()=>gotoPage(pageIndex+1)} className="btn btn-sm btn-primary mx-2 my-1" disabled={disabled || !canGoNext}>
                    <i className="bi-chevron-right text-white" />
                </button>
            </div>
        </div>
    }

    const pageInfoLabel = Localization.TABLE.PAGINA_NUMERO_DI.replace(`__NUMBER__`, pageIndex.toString()).replace(`__TOTAL__`, totalFilteredPages.toString())
    const itemsInfoLabel = (totalFiltered>=0 && totalFiltered<totalItems)?`${totalFiltered} ${Localization.TABLE.RISULTATI_FILTRATI_DA.replace("__TOTAL__", totalItems.toString())}`:`${totalItems} ${Localization.TABLE.RISULTATI}`

    return (
        <div className="d-flex flex-wrap justify-content-between align-items-center">
            <div>
                {!!pageSizeSelect && <Form className="me-3" onSubmit={e => { e.preventDefault(); }}>
                    <CSSelect disabled={disabled} onChange={(e)=>onPageSizeChange?.(parseInt(e.target.value))}>
                        {pageSizeSelect.map((pageSize, index)=>{
                            return (
                                <option key={`pagesize_${index}`} value={pageSize}>{`${pageSize.toString()} / ${Localization.TABLE.PAGINA}`}</option>
                            )
                        })}
                    </CSSelect>
                </Form>}
            </div>
            <div className="d-flex flex-wrap align-items-center">
                <span className="mr-3">{itemsInfoLabel}</span>
                <button onClick={()=>gotoPage(1)} className="btn btn-sm btn-primary mx-2 my-1" disabled={disabled || !canSkipToFirst}>
                    <i className="bi-chevron-double-left text-white" />
                </button>
                <button onClick={()=>gotoPage(pageIndex-1)} className="btn btn-sm btn-primary mx-2 my-1" disabled={disabled || !canGoPrevious}>
                    <i className="bi-chevron-left text-white" />
                </button>
                {showEllipsisPrevious && <button className="btn btn-sm border-0 btn-hover-primary mx-2 my-1" disabled>...</button>}
                {pageIndex>4 && <button disabled={disabled} onClick={()=>gotoPage(pageIndex-4)} className="btn btn-sm border-0 btn-hover-primary me-2 my-1">{pageIndex-4}</button>}
                {pageIndex>3 && <button disabled={disabled} onClick={()=>gotoPage(pageIndex-3)} className="btn btn-sm border-0 btn-hover-primary me-2 my-1">{pageIndex-3}</button>}
                {pageIndex>2 && <button disabled={disabled} onClick={()=>gotoPage(pageIndex-2)} className="btn btn-sm border-0 btn-hover-primary me-2 my-1">{pageIndex-2}</button>}
                {pageIndex>1 && <button disabled={disabled} onClick={()=>gotoPage(pageIndex-1)} className="btn btn-sm border-0 btn-hover-primary me-2 my-1">{pageIndex-1}</button>}
                <button disabled={disabled} className="btn btn-sm border-0 btn-primary me-2 my-1">{pageIndex}</button>
                {pageIndex<totalFilteredPages && <button disabled={disabled} onClick={()=>gotoPage(pageIndex+1)} className="btn btn-sm border-0 btn-hover-primary me-2 my-1">{pageIndex+1}</button>}
                {pageIndex+1<totalFilteredPages && <button disabled={disabled} onClick={()=>gotoPage(pageIndex+2)} className="btn btn-sm border-0 btn-hover-primary me-2 my-1">{pageIndex+2}</button>}
                {(pageIndex+2<totalFilteredPages && totalFilteredPages>3) && <button disabled={disabled} onClick={()=>gotoPage(pageIndex+3)} className="btn btn-sm border-0 btn-hover-primary me-2 my-1">{pageIndex+3}</button>}
                {(pageIndex+3<totalFilteredPages && totalFilteredPages>4) && <button disabled={disabled} onClick={()=>gotoPage(pageIndex+4)} className="btn btn-sm border-0 btn-hover-primary me-2 my-1">{pageIndex+4}</button>}
                {(showEllipsisNext) && <button className="btn btn-sm border-0 btn-hover-primary me-2 my-1" disabled>...</button>}

                <button onClick={()=>gotoPage(pageIndex+1)} className="btn btn-sm btn-primary mx-2 my-1" disabled={disabled || !canGoNext}>
                    <i className="bi-chevron-right text-white" />
                </button>
                <button onClick={()=>gotoPage(totalFilteredPages)} className="btn btn-sm btn-primary mx-2 my-1" disabled={disabled || !canSkipToLast}>
                    <i className="bi-chevron-double-right text-white" />
                </button>
                <span className="ml-3">{pageInfoLabel}</span>
                {enableGotoPage!=="OFF" && <div className="ms-3">
                    <CSInput 
                        controlId="pagination_goto_input"
                        disabled={disabled}
                        type="number"
                        width={'100px'}
                        value={specificPage}
                        onChange={(e)=>{
                            setSpecificPage(e.target.value)
                        }}
                        min={1} 
                        max={totalFilteredPages}
                        placeholder={Localization.TABLE.VAI_A} 
                        buttons={enableGotoPage==="BUTTON"?[
                            {
                                icon: "bi-check-circle",
                                action: ()=>{gotoSpecificPage()}
                            }
                        ]:undefined}
                    />
                    </div>}
            </div>
        </div>
    )
}

export default CSPagination